// Presentation for the seller's funding window — the clock that starts when the
// property goes live and is enforced against the offering the listing becomes.
//
// Shared because the window surfaces on two records that must read identically: the
// listing request carries it for most of its life (the property is still in owner
// onboarding, compliance or the VASP handoff), and the property carries it once an
// offering exists. Two independent copies of this logic would eventually disagree
// about what "0 days left" means.
//
// NOTHING HERE KNOWS HOW LONG A WINDOW IS. The length is per deal — whatever the
// seller's MOU and Seller Agreement printed — and arrives on every read as
// `fundingWindowDays`. The one number this file still owns is the urgency
// threshold, and even that is a ceiling scaled to the deal rather than a constant.
//
// Pure — no I/O, no DI, no framework. The server owns the numbers; this file only
// decides how they read.
import { ONE_DAY_MS, ONE_HOUR_MS, ONE_MINUTE_MS } from './datetimeUtils'

/**
 * The furthest out the window ever starts reading as urgent rather than
 * informational.
 *
 * A CEILING, NOT A CONSTANT. Windows are per deal now, so a flat 14 days against a
 * 20-day window would open the raise already amber and say nothing at all —
 * `fundingWindowUrgentDays` scales it down to a quarter of the window.
 *
 * Presentation only — nothing is enforced at this boundary, and the server neither
 * knows nor cares about it. When slice 4 adds the "closing soon" notification, it
 * must use the same number: a seller warned by email on day 76 and shown a calm card
 * on day 77 would rightly not trust either.
 */
export const FUNDING_WINDOW_URGENT_DAYS = 14

/**
 * Where the countdown turns amber for a deal on `windowDays`.
 *
 * Mirrors `fundingWindowWarningDays` in admin-web, which clamps its own (wider)
 * ceiling the same way — an operator and a seller looking at the same raise should
 * not disagree about whether it is running out of time.
 *
 * Unknown length falls back to the flat ceiling: there is nothing to scale against,
 * and in practice it does not arise — a raise with no agreement behind it has never
 * gone live, so it has no deadline to colour either.
 */
export function fundingWindowUrgentDays(windowDays: number | null | undefined): number {
  if (!windowDays) return FUNDING_WINDOW_URGENT_DAYS
  return Math.min(FUNDING_WINDOW_URGENT_DAYS, Math.ceil(windowDays / 4))
}

/**
 * `none` is a real state, not missing data: a property that never came through a
 * seller listing request has no clock and never lapses. It renders nothing at all —
 * not a zero, not an empty row.
 *
 * `notStarted`, `funded` and `notFunded` only arise where the caller knows where
 * the raise is (`FundingWindowPhase`) — the property, which shows its window from
 * setup to settlement. A request only ever has a running window or none.
 */
export type FundingWindowState =
  | 'none'
  | 'notStarted'
  | 'open'
  | 'urgent'
  | 'lapsed'
  | 'funded'
  | 'notFunded'

/**
 * Where the raise is, as the caller knows it. `running` is the default and the only
 * phase the server's deadline flags speak to; the others stop the clock whatever the
 * dates say — a funded raise is not "closing" just because its deadline is near.
 */
export type FundingWindowPhase = 'notStarted' | 'running' | 'funded' | 'notFunded'

export interface FundingWindowFields {
  fundingDeadlineAt: string | null
  fundingDaysRemaining: number | null
  isFundingWindowLapsed: boolean
  /**
   * The clock start — when the property went live. Optional because only the reads
   * that can see an offering carry it; without it the card still counts down, it
   * just cannot say how far through the window it is.
   */
  goLiveAt?: string | null
  /**
   * The length the seller's agreements promised, in days. Optional and nullable for
   * the same reason it is nullable on the wire: no document has stated one yet.
   *
   * NOT the span between `goLiveAt` and `fundingDeadlineAt` — an extension moves the
   * deadline and leaves the agreed window where it was, so an extended raise reads
   * "45-day funding window" above a bar that runs to 65.
   */
  fundingWindowDays?: number | null
}

export interface FundingWindowPresentation {
  state: FundingWindowState
  /** Whether the card renders at all. False only for `none`. */
  isVisible: boolean
  /** Drawn faded — the window exists but has not started. */
  isFaded: boolean
  tone: 'info' | 'warning' | 'danger' | 'success'
  title: string
  message: string
  /** Whether to run the live D : H : M clock. False once the window has lapsed or stopped. */
  hasLiveCountdown: boolean
}

// The deal's own number, which is the number its agreements printed. Falls back to
// the bare noun rather than to 90: a wrong length on a contractual clock is worse
// than a vaguer heading.
function windowTitleOf(fundingWindowDays: number | null | undefined): string {
  return fundingWindowDays ? `${fundingWindowDays}-day funding window` : 'Funding window'
}

// The window before and after it runs. None of these read the deadline: the phase
// already says the clock is not running.
const STOPPED_WINDOW: Record<
  Exclude<FundingWindowPhase, 'running'>,
  (fundingWindowDays: number | null | undefined) => FundingWindowPresentation
> = {
  notStarted: (fundingWindowDays) => ({
    state: 'notStarted',
    isVisible: true,
    isFaded: true,
    tone: 'info',
    title: windowTitleOf(fundingWindowDays),
    message: 'Your funding window starts when your property goes live on the Marketplace.',
    hasLiveCountdown: false,
  }),
  funded: () => ({
    state: 'funded',
    isVisible: true,
    isFaded: false,
    tone: 'success',
    title: 'Fully funded',
    message: 'Your property was fully funded within its funding window. The clock has stopped.',
    hasLiveCountdown: false,
  }),
  notFunded: () => ({
    state: 'notFunded',
    isVisible: true,
    isFaded: false,
    tone: 'danger',
    title: 'Not funded',
    message:
      'Your property wasn’t fully funded within its funding window. Our team will be in touch about what happens next.',
    hasLiveCountdown: false,
  }),
}

/**
 * The window a raise is running, in the seller's own terms.
 *
 * The state is read from the server's own flags rather than recomputed from the
 * deadline. `isFundingWindowLapsed` is the authority on whether the window has
 * closed, and `fundingDaysRemaining` is already clamped and rounded server-side —
 * re-deriving either from a browser clock would let a skewed device disagree with
 * the record.
 */
export function resolveFundingWindow(
  fields: FundingWindowFields,
  phase: FundingWindowPhase = 'running',
): FundingWindowPresentation {
  const { fundingDeadlineAt, fundingDaysRemaining, isFundingWindowLapsed, fundingWindowDays } =
    fields

  if (phase !== 'running') return STOPPED_WINDOW[phase](fundingWindowDays)

  if (!fundingDeadlineAt) {
    return {
      state: 'none',
      isVisible: false,
      isFaded: false,
      tone: 'info',
      title: '',
      message: '',
      hasLiveCountdown: false,
    }
  }

  if (isFundingWindowLapsed) {
    return {
      state: 'lapsed',
      isVisible: true,
      isFaded: false,
      tone: 'danger',
      title: 'Funding window closed',
      message:
        'The deadline to fully fund your property has passed. Our team will be in touch about what happens next.',
      hasLiveCountdown: false,
    }
  }

  const isUrgent = (fundingDaysRemaining ?? 0) <= fundingWindowUrgentDays(fundingWindowDays)
  if (isUrgent) {
    return {
      state: 'urgent',
      isVisible: true,
      isFaded: false,
      tone: 'warning',
      title: 'Funding window closing',
      message:
        'Your property is in the final stretch of its funding window. If it is not fully funded by the deadline, our team will contact you about the options.',
      hasLiveCountdown: true,
    }
  }

  return {
    state: 'open',
    isVisible: true,
    isFaded: false,
    title: windowTitleOf(fundingWindowDays),
    tone: 'info',
    message:
      'The clock started when your property went live on the Marketplace and runs until it is fully funded.',
    hasLiveCountdown: true,
  }
}

export interface FundingWindowProgress {
  /** 1-based: the day the seller is currently living through, not the day count behind them. */
  elapsedDays: number
  /** The agreed window, widened by however many days an extension added. */
  totalDays: number
  percentElapsed: number
}

/**
 * How far through the window the listing is.
 *
 * Derived from the two dates rather than from a constant, so both a per-deal window
 * and an extension are reflected without the frontend needing to know either: the
 * server writes a deadline 45 days past go-live and the bar reads "Day 12 of 45"; an
 * extension moves the deadline, which widens the span, and the bar re-scales on its
 * own.
 *
 * The start is `goLiveAt` and never `agreementCompletedAt` — everything between
 * signing and launch (owner onboarding, KYC, compliance, the VASP handoff) sits
 * OUTSIDE the window, so counting from signing would show a seller a bar half spent
 * before their listing had opened.
 *
 * Null when either end is missing — a window with no measurable span shows a
 * countdown but no progress, rather than a bar pinned at zero.
 */
export function fundingWindowProgress(
  goLiveAt: string | null | undefined,
  deadlineIso: string | null,
  now: number = Date.now(),
): FundingWindowProgress | null {
  if (!goLiveAt || !deadlineIso) return null

  const startMs = new Date(goLiveAt).getTime()
  const totalMs = new Date(deadlineIso).getTime() - startMs
  if (totalMs <= 0) return null

  const elapsedMs = Math.min(totalMs, Math.max(0, now - startMs))
  const totalDays = Math.round(totalMs / ONE_DAY_MS)
  return {
    // The first day of the window is day 1, so a listing that went live an hour ago
    // reads "Day 1 of 45" rather than day zero.
    elapsedDays: Math.min(totalDays, Math.floor(elapsedMs / ONE_DAY_MS) + 1),
    totalDays,
    percentElapsed: (elapsedMs / totalMs) * 100,
  }
}

export interface CountdownParts {
  days: number
  hours: number
  minutes: number
}

/**
 * The deadline broken into whole days, hours and minutes — the live tick.
 *
 * Deliberately separate from the server's `fundingDaysRemaining`, which rounds UP to
 * whole days so a countdown never reads zero while time remains. These floor instead,
 * because together they are a duration: 8D 14H 22M and "9 days left" are the same
 * instant described at two resolutions, and mixing the two roundings inside one
 * display would show 9D 14H 22M.
 *
 * Clamps to zero rather than going negative — a lapsed window has no countdown to
 * run, and `resolveFundingWindow` turns it off.
 */
export function countdownUntil(deadlineIso: string, now: number = Date.now()): CountdownParts {
  const remainingMs = Math.max(0, new Date(deadlineIso).getTime() - now)
  return {
    days: Math.floor(remainingMs / ONE_DAY_MS),
    hours: Math.floor((remainingMs % ONE_DAY_MS) / ONE_HOUR_MS),
    minutes: Math.floor((remainingMs % ONE_HOUR_MS) / ONE_MINUTE_MS),
  }
}

/** "8D : 14H : 22M" */
export function formatCountdown(parts: CountdownParts): string {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${parts.days}D : ${pad(parts.hours)}H : ${pad(parts.minutes)}M`
}
